import { Download, Share, X } from "lucide-react";
import { useCallback, useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";

interface BeforeInstallPromptChoice {
  outcome: "accepted" | "dismissed";
  platform: string;
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<BeforeInstallPromptChoice>;
  prompt: () => Promise<void>;
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

export interface PwaInstallPromptProps {
  /** Permet de désactiver l'invitation sans démonter le composant. */
  enabled?: boolean;
}

type InstallMode = "native" | "ios";

const DISMISSED_AT_KEY = "skyjo:pwa-install-dismissed-at";
const DISMISSAL_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  Boolean((navigator as NavigatorWithStandalone).standalone);

const isIos = () => {
  const userAgent = navigator.userAgent;
  return /iPad|iPhone|iPod/i.test(userAgent) ||
    (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1);
};

const isMobile = () => {
  const mobileUserAgent = /Android|iPad|iPhone|iPod|IEMobile|Mobile/i.test(
    navigator.userAgent,
  );
  const compactTouchDevice =
    window.matchMedia("(pointer: coarse)").matches &&
    Math.min(window.screen.width, window.screen.height) <= 1024;

  return mobileUserAgent || compactTouchDevice;
};

const wasRecentlyDismissed = () => {
  try {
    const dismissedAt = Number(window.localStorage.getItem(DISMISSED_AT_KEY));
    return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < DISMISSAL_DURATION_MS;
  } catch {
    return false;
  }
};

const rememberDismissal = () => {
  try {
    window.localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
  } catch {
    // Le stockage peut être indisponible en navigation privée : fermer suffit pour cette session.
  }
};

export const PwaInstallPrompt = ({ enabled = true }: PwaInstallPromptProps) => {
  const titleId = useId();
  const descriptionId = useId();
  const [mode, setMode] = useState<InstallMode | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalling, setIsInstalling] = useState(false);

  const close = useCallback(() => {
    rememberDismissal();
    setMode(null);
  }, []);

  useEffect(() => {
    if (!isMobile() || isStandalone()) return undefined;

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };

    const handleInstalled = () => {
      try {
        window.localStorage.removeItem(DISMISSED_AT_KEY);
      } catch {
        // Aucun nettoyage n'est nécessaire si le stockage n'est pas disponible.
      }
      setDeferredPrompt(null);
      setMode(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  useEffect(() => {
    if (!enabled || !isMobile() || isStandalone() || wasRecentlyDismissed()) {
      setMode(null);
      return;
    }

    if (deferredPrompt) {
      setMode("native");
    } else if (isIos()) {
      setMode("ios");
    }
  }, [deferredPrompt, enabled]);

  const install = async () => {
    if (!deferredPrompt || isInstalling) return;

    setIsInstalling(true);
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);

      if (choice.outcome === "accepted") {
        setMode(null);
      } else {
        close();
      }
    } catch {
      setDeferredPrompt(null);
      setMode(null);
    } finally {
      setIsInstalling(false);
    }
  };

  if (!enabled || mode === null) return null;

  return createPortal(
    <>
      <style>{`
        .skyjo-pwa-prompt {
          position: fixed;
          z-index: 90;
          right: max(14px, env(safe-area-inset-right));
          bottom: max(14px, env(safe-area-inset-bottom));
          left: max(14px, env(safe-area-inset-left));
          display: grid;
          grid-template-columns: auto minmax(0, 1fr) auto;
          gap: 12px;
          align-items: start;
          max-width: 520px;
          margin-inline: auto;
          padding: 16px;
          color: #f4fffb;
          background: linear-gradient(145deg, rgba(10, 74, 78, 0.98), rgba(4, 39, 48, 0.98));
          border: 1px solid rgba(135, 235, 215, 0.32);
          border-radius: 22px;
          box-shadow: 0 18px 50px rgba(0, 10, 15, 0.58);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          font: inherit;
          animation: skyjo-pwa-prompt-in 260ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
        }

        .skyjo-pwa-prompt__icon {
          display: grid;
          width: 44px;
          height: 44px;
          place-items: center;
          color: #092f37;
          background: #ffd45c;
          border-radius: 14px;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
        }

        .skyjo-pwa-prompt__copy {
          min-width: 0;
        }

        .skyjo-pwa-prompt__title {
          margin: 1px 0 4px;
          font-size: 1rem;
          font-weight: 900;
          line-height: 1.2;
        }

        .skyjo-pwa-prompt__description {
          margin: 0;
          color: rgba(235, 255, 250, 0.78);
          font-size: 0.82rem;
          line-height: 1.4;
        }

        .skyjo-pwa-prompt__ios-step {
          display: flex;
          gap: 7px;
          align-items: center;
          margin-top: 9px;
          color: #fff4c4;
          font-size: 0.8rem;
          font-weight: 750;
          line-height: 1.35;
        }

        .skyjo-pwa-prompt__close {
          display: grid;
          width: 44px;
          height: 44px;
          padding: 0;
          place-items: center;
          color: rgba(244, 255, 251, 0.78);
          background: rgba(255, 255, 255, 0.07);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 50%;
          cursor: pointer;
        }

        .skyjo-pwa-prompt__install {
          grid-column: 2 / -1;
          justify-self: start;
          min-height: 44px;
          padding: 0 18px;
          color: #0a2d34;
          background: linear-gradient(135deg, #ffe27a, #ffc84e);
          border: 0;
          border-radius: 13px;
          box-shadow: 0 7px 20px rgba(255, 198, 66, 0.2);
          font: inherit;
          font-size: 0.86rem;
          font-weight: 900;
          cursor: pointer;
        }

        .skyjo-pwa-prompt__install:disabled {
          cursor: wait;
          opacity: 0.65;
        }

        .skyjo-pwa-prompt__close:focus-visible,
        .skyjo-pwa-prompt__install:focus-visible {
          outline: 3px solid #ffffff;
          outline-offset: 3px;
        }

        @keyframes skyjo-pwa-prompt-in {
          from { opacity: 0; transform: translateY(24px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        @media (min-width: 901px) {
          .skyjo-pwa-prompt { display: none; }
        }

        @media (prefers-reduced-motion: reduce) {
          .skyjo-pwa-prompt { animation: none; }
        }
      `}</style>
      <aside
        className="skyjo-pwa-prompt"
        role="dialog"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        aria-live="polite"
      >
        <span className="skyjo-pwa-prompt__icon" aria-hidden="true">
          <Download size={22} strokeWidth={2.6} />
        </span>

        <div className="skyjo-pwa-prompt__copy">
          <h2 className="skyjo-pwa-prompt__title" id={titleId}>
            Installer Skyjo
          </h2>
          <p className="skyjo-pwa-prompt__description" id={descriptionId}>
            Ajoute le jeu à ton écran d'accueil pour le lancer en plein écran, comme une app.
          </p>
          {mode === "ios" && (
            <div className="skyjo-pwa-prompt__ios-step">
              <Share size={17} aria-hidden="true" />
              Touche Partager, puis « Sur l'écran d'accueil ».
            </div>
          )}
        </div>

        <button
          className="skyjo-pwa-prompt__close"
          type="button"
          aria-label="Me le rappeler plus tard"
          onClick={close}
        >
          <X size={19} aria-hidden="true" />
        </button>

        {mode === "native" && (
          <button
            className="skyjo-pwa-prompt__install"
            type="button"
            disabled={isInstalling}
            aria-busy={isInstalling}
            onClick={() => void install()}
          >
            {isInstalling ? "Installation…" : "Installer l'application"}
          </button>
        )}
      </aside>
    </>,
    document.body,
  );
};
